import { memo } from 'react';

import moment from 'moment';

import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import type { TextView } from '@/components/ui/widget/views/text/text.view.type';
import type { WidgetData } from '@/components/ui/widget/widget.type';
import { formatNumber, getByPath } from '@/utils';
import type { DataTabView } from './dataTab.view.type';

type Props = {
  view: DataTabView;
  data: WidgetData;
};

/**
 * Basic interface to display some summary information about a piece of data
 * should serve as an intermediary step for eg a modal display of detailed data
 */

export const DataTabViewImpl = memo(({ view, data }: Props) => {
  const { namePath, iconPath, path, editedByPath, editedAtPath } = view;

  // concrete data label and value (to get the length and then more..)
  const dataName = getByPath<string>(data, namePath) || 'Data';
  const dataIcon = getByPath<string>(data, iconPath) || 'lucide_Table2';
  const dataValue = getByPath<any>(data, path);

  // display last activity on this data
  const editedBy = getByPath<string>(data, editedByPath);
  const editedAt = getByPath<string>(data, editedAtPath);

  // return nothing if dataValue does not exist
  if (dataValue === undefined) return null;

  // handle limited set of data types for meta info, otherwise stringify
  let dataInfoMetaMsg = dataValue.toString();
  if (typeof dataValue === 'object') {
    dataInfoMetaMsg = Array.isArray(dataValue)
      ? `${formatNumber(dataValue.length)} rows`
      : `${formatNumber(Object.keys(dataValue).length)} values`;
  }

  return (
    <div className="flex flex-row flex-nowrap items-center justify-between gap-5">
      <div className="flex flex-row flex-nowrap items-center justify-start gap-4">
        <div className="text-primary">
          <DynamicIcon icon={dataIcon} size={20} />
        </div>
        <div className="flex flex-col flex-nowrap items-start gap-0.5">
          <div className="text-xs">{dataName}</div>
          <div className="text-[10px] text-muted-foreground">
            {dataInfoMetaMsg}
          </div>
        </div>
      </div>
      <div className="flexs-col flex flex-nowrap items-end gap-1">
        <div className="text-xs">{editedBy}</div>
        <div className="text-[10px] text-muted-foreground">
          {editedAt &&
            `Last update: ${moment(editedAt).format('DD/MM/YYYY LT')}`}
        </div>
      </div>
    </div>
  );
});

// sort defaults to text on name
const getSortConfig = (view: DataTabView): TextView => ({
  type: 'text',
  path: view.namePath
});

// filtering defaults to text search on name
const getFilterConfig = (view: DataTabView): TextView => ({
  type: 'text',
  path: view.namePath
});

// width defaults to 'large'
const getDefaultWidth = (): ColumnWidth => 'large' as const;

export const dataTabViewProvider = {
  component: DataTabViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth
};
